import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Bookmark } from 'lucide-react';
import { useTranslations } from 'next-intl';
import type {
  SearchSubscriptionResponse,
  UpdateSearchSubscription,
  UpdateSearchSubscriptionResponse,
} from '@//app/api/protected/subscriptions/search/[id]';
import type { EnrichedSearchSubscriptionResponse } from '@//app/api/protected/subscriptions/search/enriched';
import { useAuth } from '@//providers/AuthProvider';
import { useApiBaseUrl } from '@//providers/MarketSettingsProvider';
import { ToastTypeEnum, useToast } from '@//providers/ToastProvider';
import { httpClientPutAuthorizedJson } from '../../httpClient';
import { SEARCH_SUBSCRIPTIONS_ENRICHED_CACHE_KEY } from './useEnrichedSearchSubscriptions';
import { SEARCH_SUBSCRIPTION_CACHE_KEY } from './useSearchSubscription';

export interface UpdateSearchSubscriptionVariables {
  id: string;
  silent?: boolean;
  body: UpdateSearchSubscription;
}

const useUpdateSearchSubscription = () => {
  const t = useTranslations('other.toast.subscription.search.update');
  const { decodedToken } = useAuth();
  const apiBaseUrl = useApiBaseUrl();
  const queryClient = useQueryClient();
  const { addToast } = useToast();

  const mutation = useMutation<
    UpdateSearchSubscriptionResponse,
    Error,
    UpdateSearchSubscriptionVariables
  >({
    mutationFn: (variables) => {
      const url = `${apiBaseUrl}/api/protected/subscriptions/search/${variables.id}`;
      return httpClientPutAuthorizedJson(url, variables.body);
    },
    onSuccess: (data, variables) => {
      queryClient.setQueryData<SearchSubscriptionResponse>(
        [SEARCH_SUBSCRIPTION_CACHE_KEY, decodedToken?.sub, variables.id],
        {
          successful: true,
          response: data.response,
        },
      );

      const next = queryClient.getQueryData<EnrichedSearchSubscriptionResponse>([
        SEARCH_SUBSCRIPTIONS_ENRICHED_CACHE_KEY,
        decodedToken?.sub,
      ]);
      if (next) {
        let invalidate = true;
        const previousIndex = next.enrichedSubscriptions.findIndex(
          (x) => x.subscription.id === variables.id,
        );
        if (previousIndex > -1) {
          const previousQuery = next.enrichedSubscriptions[previousIndex].subscription.query;
          if (previousQuery === variables.body.query) {
            next.enrichedSubscriptions[previousIndex] = {
              ...next.enrichedSubscriptions[previousIndex],
              subscription: {
                ...next.enrichedSubscriptions[previousIndex].subscription,
                name: variables.body.name,
              },
              vehicleResult: undefined,
            };
            queryClient.setQueryData<EnrichedSearchSubscriptionResponse>(
              [SEARCH_SUBSCRIPTIONS_ENRICHED_CACHE_KEY, decodedToken?.sub],
              next,
            );
            // Name was only updated, no need to invalidate
            invalidate = false;
          }
        }

        if (invalidate) {
          queryClient.invalidateQueries({
            queryKey: [SEARCH_SUBSCRIPTIONS_ENRICHED_CACHE_KEY, decodedToken?.sub],
          });
        }
      } else {
        queryClient.invalidateQueries({
          queryKey: [SEARCH_SUBSCRIPTIONS_ENRICHED_CACHE_KEY, decodedToken?.sub],
        });
      }
      if (variables.silent) return;
      addToast({
        type: ToastTypeEnum.Success,
        icon: <Bookmark aria-hidden="true" />,
        title: t('success.title'),
        description: t('success.description'),
      });
    },
    onError: (_, variables) => {
      if (variables.silent) return;
      addToast({
        type: ToastTypeEnum.Error,
        title: t('error.title'),
        description: t('error.description'),
      });
    },
  });

  return mutation;
};

export default useUpdateSearchSubscription;
